'use client'
import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIsLoggedInQuery } from '@repo/analytics/hooks/use-is-logged-in-query'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { VoteButton } from '../action-buttons/vote-button'
import {
  useSocialGetPostQuery,
  socialCuratedFeedQueryRootKey,
  socialGetPostQueryRootKey,
} from '@web/gen/feed'
import {
  useSocialFeedPostAddLikeMutation,
  useSocialFeedPostRemoveLikeMutation,
} from '@web/gen/feed-post'

interface Props {
  upvotes: number
  postId: string
}

export function VoteProjectButton({ upvotes, postId }: Props) {
  const postIdNum = React.useMemo(() => Number(postId), [postId])

  const { openModal } = useUserAuthModal()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const queryClient = useQueryClient()

  const socialFeedPostAddLikeMutation = useSocialFeedPostAddLikeMutation()
  const socialFeedPostRemoveLikeMutation = useSocialFeedPostRemoveLikeMutation()

  const { data: initialIsLiked } = useSocialGetPostQuery(
    { input: { postId } },
    { select: d => d?.socialGetPost?.post?.isLiked },
  )

  const [isLiked, setIsLiked] = React.useState<boolean>(!!initialIsLiked)
  const [localUpvotes, setLocalUpvotes] = React.useState<number>(upvotes)

  React.useEffect(() => {
    if (typeof initialIsLiked === 'boolean') {
      setIsLiked(initialIsLiked)
    }
  }, [initialIsLiked])

  const toggleLike = React.useCallback(
    async (currentlyLiked: boolean) => {
      if (currentlyLiked) {
        await socialFeedPostRemoveLikeMutation.mutateAsync({ input: { postId: postIdNum } })
      } else {
        await socialFeedPostAddLikeMutation.mutateAsync({ input: { postId: postIdNum } })
      }
    },
    [socialFeedPostAddLikeMutation, socialFeedPostRemoveLikeMutation, postIdNum],
  )

  const { mutate } = useMutation({
    mutationKey: ['post-like-toggle', postIdNum],
    mutationFn: (currentlyLiked: boolean) => toggleLike(currentlyLiked),

    onMutate: async (currentlyLiked: boolean) => {
      await queryClient.cancelQueries({ queryKey: [socialGetPostQueryRootKey] })
      await queryClient.cancelQueries({
        queryKey: [`${socialCuratedFeedQueryRootKey}.Infinite`],
      })

      const prevIsLiked = isLiked
      const prevUpvotes = localUpvotes

      const nextLiked = !currentlyLiked
      const nextUpvotes = Math.max(0, currentlyLiked ? prevUpvotes - 1 : prevUpvotes + 1)

      setIsLiked(nextLiked)
      setLocalUpvotes(nextUpvotes)

      return { prevIsLiked, prevUpvotes }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [socialGetPostQueryRootKey] })
      queryClient.invalidateQueries({
        queryKey: [`${socialCuratedFeedQueryRootKey}.Infinite`],
      })
    },
  })

  const handleClick = React.useCallback(() => {
    if (!isLoggedIn) {
      openModal('register')
      return
    }
    mutate(isLiked)
  }, [isLoggedIn, openModal, mutate, isLiked])

  return (
    <div
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
      className="pointer-events-auto"
    >
      <VoteButton isLiked={isLiked} upvotes={localUpvotes} onClick={handleClick} />
    </div>
  )
}
